/**
 * LocationService - Manages user location detection and caching
 * Handles Geolocation API interactions with comprehensive error handling
 */

import { LocationService as LocationServiceInterface } from './interfaces/index.js';

/**
 * LocationService implementation with Geolocation API integration
 */
export class LocationService extends LocationServiceInterface {
  constructor(config = {}) {
    super();
    
    // Configuration with defaults
    this.config = {
      timeout: config.timeout || 10000, // 10 seconds default
      maximumAge: config.maximumAge || 300000, // 5 minutes default
      enableHighAccuracy: config.enableHighAccuracy !== false, // enabled by default
      minAccuracy: config.minAccuracy || 1000, // 1km minimum accuracy
      maxRetries: config.maxRetries || 3,
      retryDelay: config.retryDelay || 2000, // 2 seconds between retries
      ...config
    };
    
    // Internal state
    this.callbacks = [];
    this.watchId = null;
    this.lastKnownLocation = null;
    this.permissionStatus = null;
    this.isWatching = false;
    this.retryCount = 0;
    
    // Initialize permission monitoring if available
    this.initializePermissionMonitoring();
  }

  /**
   * Get current location from GPS with error handling and retry logic
   * @returns {Promise<Coordinates>} Promise resolving to current coordinates
   */
  async getCurrentLocation() {
    // Check if geolocation is supported
    if (!this.isGeolocationSupported()) {
      throw new Error('Geolocation is not supported by this browser');
    }

    // Try to get cached location first if it's recent enough
    const cachedLocation = this.getCachedLocation();
    if (cachedLocation && this.isLocationFresh(cachedLocation)) {
      return cachedLocation;
    }

    // Request location with retry logic
    return this.requestLocationWithRetry();
  }

  /**
   * Get cached location if available and valid
   * @returns {Coordinates|null} Cached coordinates or null
   */
  getCachedLocation() {
    return this.lastKnownLocation;
  }

  /**
   * Request location permission from user
   * @returns {Promise<boolean>} Promise resolving to permission granted status
   */
  async requestLocationPermission() {
    if (!this.isGeolocationSupported()) {
      return false;
    }

    // Check if Permissions API is available
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        this.permissionStatus = permission.state;
        
        // Listen for permission changes
        permission.addEventListener('change', () => {
          this.permissionStatus = permission.state;
          this.handlePermissionChange(permission.state);
        });
        
        return permission.state === 'granted';
      } catch (error) {
        console.warn('Permissions API query failed:', error);
      }
    }

    // Fallback: try to get location to trigger permission prompt
    try {
      await this.requestSingleLocation();
      return true;
    } catch (error) {
      if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
        this.permissionStatus = 'denied';
        return false;
      }
      // Other errors don't necessarily mean permission denied
      return false;
    }
  }

  /**
   * Register callback for location updates
   * @param {function(Coordinates): void} callback - Callback function for location updates
   */
  onLocationUpdate(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    this.callbacks.push(callback);
  }

  /**
   * Remove a callback from location updates
   * @param {function(Coordinates): void} callback - Callback function to remove
   */
  removeLocationUpdateCallback(callback) {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * Start watching location changes
   * @returns {Promise<void>}
   */
  async startWatching() {
    if (this.isWatching || !this.isGeolocationSupported()) {
      return;
    }

    const options = {
      enableHighAccuracy: this.config.enableHighAccuracy,
      timeout: this.config.timeout,
      maximumAge: this.config.maximumAge
    };

    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.handleLocationSuccess(position),
      (error) => this.handleLocationError(error),
      options
    );

    this.isWatching = true;
  }

  /**
   * Stop watching location changes
   */
  stopWatching() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.isWatching = false;
  }

  /**
   * Get current permission status
   * @returns {string|null} Permission status or null if unknown
   */
  getPermissionStatus() {
    return this.permissionStatus;
  }

  /**
   * Check if location service is available
   * @returns {boolean} True if geolocation is supported
   */
  isGeolocationSupported() {
    return 'geolocation' in navigator;
  }

  /**
   * Get last known location timestamp
   * @returns {Date|null} Timestamp of last known location or null
   */
  getLastLocationTime() {
    return this.lastKnownLocation ? this.lastKnownLocation.timestamp : null;
  }

  /**
   * Check if watching location changes
   * @returns {boolean} True if currently watching
   */
  isWatchingLocation() {
    return this.isWatching;
  }

  // Private methods

  /**
   * Request location with retry logic
   * @private
   * @returns {Promise<Coordinates>}
   */
  async requestLocationWithRetry() {
    this.retryCount = 0;
    
    while (this.retryCount < this.config.maxRetries) {
      try {
        const location = await this.requestSingleLocation();
        
        // Validate location accuracy
        if (this.isLocationAccurate(location)) {
          this.retryCount = 0; // Reset retry count on success
          return location;
        } else {
          console.warn(`Location accuracy too low: ${location.accuracy}m, retrying...`);
          this.retryCount++;
          
          if (this.retryCount < this.config.maxRetries) {
            await this.delay(this.config.retryDelay);
          }
        }
      } catch (error) {
        // Don't retry for permission denied or unsupported errors
        if (error.code === GeolocationPositionError.PERMISSION_DENIED ||
            error.message.includes('not supported')) {
          throw error;
        }
        
        this.retryCount++;
        
        if (this.retryCount < this.config.maxRetries) {
          console.warn(`Location request failed (attempt ${this.retryCount}), retrying:`, error.message);
          await this.delay(this.config.retryDelay * this.retryCount); // Exponential backoff
        } else {
          // If we have a cached location, return it as fallback
          if (this.lastKnownLocation) {
            console.warn('Using cached location after retry failures');
            return this.lastKnownLocation;
          }
          
          // Re-throw the original error instead of a generic retry message
          throw error;
        }
      }
    }
    
    // If we have a cached location, return it as fallback
    if (this.lastKnownLocation) {
      console.warn('Using cached location after retry failures');
      return this.lastKnownLocation;
    }
    
    throw new Error(`Failed to get location after ${this.config.maxRetries} attempts`);
  }

  /**
   * Request a single location
   * @private
   * @returns {Promise<Coordinates>}
   */
  requestSingleLocation() {
    return new Promise((resolve, reject) => {
      const options = {
        enableHighAccuracy: this.config.enableHighAccuracy,
        timeout: this.config.timeout,
        maximumAge: this.config.maximumAge
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = this.createCoordinatesFromPosition(position);
          resolve(coordinates);
        },
        (error) => {
          reject(this.createLocationError(error));
        },
        options
      );
    });
  }

  /**
   * Handle successful location retrieval
   * @private
   * @param {GeolocationPosition} position - Geolocation position
   */
  handleLocationSuccess(position) {
    const coordinates = this.createCoordinatesFromPosition(position);
    
    // Update last known location
    this.lastKnownLocation = coordinates;
    
    // Notify all callbacks
    this.callbacks.forEach(callback => {
      try {
        callback(coordinates);
      } catch (error) {
        console.error('Error in location update callback:', error);
      }
    });
  }

  /**
   * Handle location error
   * @private
   * @param {GeolocationPositionError} error - Geolocation error
   */
  handleLocationError(error) {
    const locationError = this.createLocationError(error);
    console.error('Location error:', locationError.message);
    
    // Update permission status if it's a permission error
    if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
      this.permissionStatus = 'denied';
    }
  }

  /**
   * Create coordinates object from GeolocationPosition
   * @private
   * @param {GeolocationPosition} position - Geolocation position
   * @returns {Coordinates} Coordinates object
   */
  createCoordinatesFromPosition(position) {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: new Date(position.timestamp)
    };
  }

  /**
   * Create location error with enhanced information
   * @private
   * @param {GeolocationPositionError} error - Geolocation error
   * @returns {Error} Enhanced error object
   */
  createLocationError(error) {
    let message;
    let code = error.code;
    
    switch (error.code) {
      case GeolocationPositionError.PERMISSION_DENIED:
        message = 'Location access denied by user';
        break;
      case GeolocationPositionError.POSITION_UNAVAILABLE:
        message = 'Location information unavailable';
        break;
      case GeolocationPositionError.TIMEOUT:
        message = 'Location request timed out';
        break;
      default:
        message = 'Unknown location error';
    }
    
    const locationError = new Error(message);
    locationError.code = code;
    locationError.originalError = error;
    
    return locationError;
  }

  /**
   * Check if location is accurate enough
   * @private
   * @param {Coordinates} coordinates - Coordinates to check
   * @returns {boolean} True if location is accurate enough
   */
  isLocationAccurate(coordinates) {
    return coordinates.accuracy <= this.config.minAccuracy;
  }

  /**
   * Check if cached location is still fresh
   * @private
   * @param {Coordinates} coordinates - Coordinates to check
   * @returns {boolean} True if location is fresh
   */
  isLocationFresh(coordinates) {
    if (!coordinates || !coordinates.timestamp) {
      return false;
    }
    
    const age = Date.now() - coordinates.timestamp.getTime();
    return age < this.config.maximumAge;
  }

  /**
   * Initialize permission monitoring if available
   * @private
   */
  async initializePermissionMonitoring() {
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        this.permissionStatus = permission.state;
        
        permission.addEventListener('change', () => {
          this.permissionStatus = permission.state;
          this.handlePermissionChange(permission.state);
        });
      } catch (error) {
        console.warn('Could not initialize permission monitoring:', error);
      }
    }
  }

  /**
   * Handle permission status changes
   * @private
   * @param {string} state - New permission state
   */
  handlePermissionChange(state) {
    console.log('Location permission changed to:', state);
    
    if (state === 'granted' && !this.isWatching) {
      // Permission was granted, we could start watching if needed
      // This is handled by the application logic
    } else if (state === 'denied' && this.isWatching) {
      // Permission was denied, stop watching
      this.stopWatching();
    }
  }

  /**
   * Utility method to create a delay
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopWatching();
    this.callbacks = [];
    this.lastKnownLocation = null;
  }
}

export default LocationService;